import { Button } from "@bcc/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@bcc/lib/utils";
import {
  setModalNavigationTestId,
  setModalNavigationBackButtonTestId,
  setModalNavigationContinueButtonTestId,
  setModalNavigationImportButtonTestId,
} from "./modal-navigation.utils";

export type Step = "select" | "configure" | "review";

export interface ModalNavigationProps {
  step: Step;
  onBack: () => void;
  onContinue: () => void;
  onImport: () => void;
  importing?: boolean;
  canContinue?: boolean;
  canImport?: boolean;
  backLabel?: string;
  continueLabel?: string;
  importLabel?: string;
  testId?: string;
  className?: string;
}

export function ModalNavigation({
  step,
  onBack,
  onContinue,
  onImport,
  importing = false,
  canContinue = true,
  canImport = true,
  backLabel = "Back",
  continueLabel = "Continue",
  importLabel = "Import Product",
  testId,
  className,
}: Readonly<ModalNavigationProps>) {
  // Review step has no navigation buttons
  if (step === "review") {
    return null;
  }

  // Select step: only Continue button (right-aligned)
  if (step === "select") {
    return (
      <div data-testid={testId ?? setModalNavigationTestId("default")} className={cn("flex justify-end pt-4", className)}>
        <Button data-testid={setModalNavigationContinueButtonTestId("default")} onClick={onContinue} disabled={!canContinue}>
          {continueLabel}
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Configure step: Back + Import buttons (space-between). A Back affordance
  // also lives at the TOP of the Configure step per the mockup.
  if (step === "configure") {
    return (
      <div data-testid={testId ?? setModalNavigationTestId("default")} className={cn("flex justify-between pt-4", className)}>
        <Button data-testid={setModalNavigationBackButtonTestId("default")} variant="outline" onClick={onBack}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          {backLabel}
        </Button>
        <Button data-testid={setModalNavigationImportButtonTestId("default")} onClick={onImport} disabled={importing || !canImport}>
          {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {importLabel}
        </Button>
      </div>
    );
  }

  return null;
}
